<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, ref, watch } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { tradingApi } from '@/api/trading'
import { apiRequest, errorMessage } from '@/api/http'
import { clearPending, pendingRevision, readPending, readPendingForRecovery } from '@/utils/pendingOperation'
import type { Order, WalletFundRequest } from '@/types/trading'
const auth = useAuthStore()
const pending = computed(() => { void pendingRevision.value; return readPending(auth.user?.id) })
const busy = ref(false)
const message = ref('')
let disposed = false
watch(() => [auth.user?.id, auth.sessionRevision], () => { message.value = ''; busy.value = false }, { flush: 'sync' })
const onStorage = (event: StorageEvent) => { if (event.key === null || event.key.startsWith('btp.pending-operation.')) pendingRevision.value++ }
onMounted(() => window.addEventListener('storage', onStorage))
onBeforeUnmount(() => { disposed = true; window.removeEventListener('storage', onStorage) })
async function recover(retry = false) {
  const operation = pending.value
  if (!operation || busy.value) return
  const revision = auth.sessionRevision
  const isCurrent = () => !disposed && auth.user?.id === operation.userId && auth.isCurrentSession(revision)
  if (!isCurrent()) return
  busy.value = true
  message.value = ''
  try {
    const stored = await readPendingForRecovery(operation.userId, operation.key)
    if (!isCurrent()) return
    if (!stored) { pendingRevision.value++; return }
    const lookup = await apiRequest<{ found: boolean; record?: Order | WalletFundRequest }>({ method: 'GET', url: operation.kind === 'order' ? '/orders/by-request-key' : '/wallets/fund-requests/by-request-key', params: { key: operation.key } })
    const result = lookup.record
    if (!isCurrent()) return
    if (lookup.found && result) {
      await clearPending(operation.userId, operation.key, isCurrent)
      if (!isCurrent()) return
      message.value = `已確認紀錄：${'orderNo' in result ? result.orderNo : result.requestNo}。請至訂單或資金申請核對。`
      window.dispatchEvent(new Event('trading:recovered'))
    } else if (retry) {
      const replay = operation.kind === 'order' ? await tradingApi.createOrder(operation.payload, operation.key) : await tradingApi.createFundRequest(operation.payload)
      if (!isCurrent()) return
      await clearPending(operation.userId, operation.key, isCurrent)
      if (!isCurrent()) return
      message.value = `已確認紀錄：${'orderNo' in replay ? replay.orderNo : replay.requestNo}。`
      window.dispatchEvent(new Event('trading:recovered'))
    } else message.value = '暫未查到結果，原請求可能仍在處理。請稍後再查，或使用原請求重新確認，勿重複建立新單。'
  } catch (cause) {
    if (isCurrent()) message.value = `${errorMessage(cause)} 原操作結果仍待核對，已保留原請求。`
  } finally { if (isCurrent()) busy.value = false }
}
async function cancelOriginal() {
  const operation = pending.value
  if (!operation || busy.value) return
  const revision = auth.sessionRevision
  const isCurrent = () => !disposed && auth.user?.id === operation.userId && auth.isCurrentSession(revision)
  if (!isCurrent()) return
  busy.value = true
  message.value = ''
  try {
    const stored = await readPendingForRecovery(operation.userId, operation.key)
    if (!isCurrent()) return
    if (!stored) { pendingRevision.value++; return }
    const result = await apiRequest<{ cancelled: boolean }>({ method: 'POST', url: '/wallets/pending-operations/cancel', data: { kind: operation.kind.toUpperCase(), key: operation.key } })
    if (!isCurrent()) return
    if (!result.cancelled) { message.value = '原請求已有紀錄，無法取消，請查詢原請求結果。'; return }
    await clearPending(operation.userId, operation.key, isCurrent)
    if (isCurrent()) message.value = '原請求已取消。'
  } catch (cause) {
    if (isCurrent()) message.value = `${errorMessage(cause)} 取消結果待確認，已保留原請求。`
  } finally { if (isCurrent()) busy.value = false }
}
</script>
<template>
  <section v-if="pending || message" class="panel" role="status">
    <template v-if="pending">
      <strong>操作結果待確認</strong>
      <div class="pending-actions"><button class="button button--quiet" :disabled="busy" @click="recover()">查詢原請求結果</button><button class="button button--quiet" :disabled="busy" @click="recover(true)">使用原請求重新確認</button><button class="button button--quiet" :disabled="busy" @click="cancelOriginal()">取消原請求</button></div>
    </template>
    <p v-if="message">{{ message }}</p>
  </section>
</template>
<style scoped>.pending-actions { display: flex; flex-wrap: wrap; gap: 8px; }</style>
